import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { WebdisplayPageTitleStrategy } from './core/title-strategy/webdisplay-title-strategy';
import { I_GridLayout, E_HomeLayoutMode, ResponsiveService } from './core/utils/responsive.service';
import { EDialogStatus, OverlayService } from "@adas/ng-overlay";
import { FullScreenService } from './core/utils/fullscreen.service';
import { InfoDialogComponent } from './modules/main/pages/standard/components/dialog-items/info-dialog/info-dialog.component';
import { E_DisplayBehavior, DisplayBehaviorService } from './core/utils/display-behavior.service';
import { E_DisplayMode } from './core/enums/displays/display-mode.enum';
import { E_KioskMode } from './core/enums/displays/kiosk-mode.enum';
import { DisplayModeService } from './core/services/display-mode.service';
import { ThemeService } from './core/utils/theme-service.service';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent {
  private route = inject(ActivatedRoute);
  private displayModeService = inject(DisplayModeService);
  private fullScreenService = inject(FullScreenService);
  private overlay = inject(OverlayService);
  private cdr = inject(ChangeDetectorRef);
  private titleStrategy = inject(WebdisplayPageTitleStrategy);
  private responsive = inject(ResponsiveService);
  private displayBehavior = inject(DisplayBehaviorService);
  private _theme = inject(ThemeService);

  private destroy$: Subject<void> = new Subject<void>();
  private queryParamsSub?: Subscription;
  title = 'Adas WebBoard';

  ngOnInit(): void {
    this.setupTitle();
    this.initQueryParamsSubscription();
  }

  constructor() {
    this._theme;
  }

  private initQueryParamsSubscription(): void {
    this.queryParamsSub?.unsubscribe();
    this.queryParamsSub = this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(async (params: Params) => {

      if (params['kiosk']) {
        await this.handleKioskMode(params['kiosk']);
      }

      if (params['displayMode'] || params['size'] || params['columns'] || params['rows']) {
        this.handleLayoutSettings(params);
      }

      if (params['token']) {
        this.handleAuthToken(params['token']);
      }

      if (params.hasOwnProperty('isDemoMode')) {
        this.handleDemoMode(params);
      }

      this.cdr.markForCheck();
    });
  }

  private async handleKioskMode(kioskParam: string): Promise<void> {
    const mode = this.mapKioskMode(kioskParam.toLowerCase());
    this.displayModeService.setCurrentDisplayMode(mode);
    if (mode === E_DisplayMode.Default) return;

    const fullScreenEntered = await this.fullScreenService.enterFullScreen();

    if (!fullScreenEntered) {
      this.showKioskInfoDialog();
    }
  }

  private mapKioskMode(kiosk: string): E_DisplayMode {
    const config: Record<string, E_DisplayMode> = {
      [E_KioskMode.Box]: E_DisplayMode.KioskBox,
      [E_KioskMode.HibridBox]: E_DisplayMode.KioskHibridBox,
      [E_KioskMode.Section]: E_DisplayMode.KioskSection,
      [E_KioskMode.HibridSection]: E_DisplayMode.KioskHibridSection,
    };
    return config[kiosk] || E_DisplayMode.Default;
  }

  private handleLayoutSettings(params: Params): void {
    const modeParam = (params['displayMode'] || '').toLowerCase();
    const isGrid = modeParam === E_HomeLayoutMode.Grid;

    this.responsive.setHomeLayoutModeStatus(isGrid ? E_HomeLayoutMode.Grid : E_HomeLayoutMode.Fluid);

    if (isGrid) {
      this.applyGridLayout(params);
    }
  }

  private handleDemoMode(params: Params): void {
    let isDemoMode;
    try {
      isDemoMode = Boolean(JSON.parse(((params['isDemoMode']?.toLowerCase() || false))));
    } catch (error) {
      isDemoMode = false;
    }
    this.displayBehavior.setDisplayBehavior(isDemoMode ? E_DisplayBehavior.Demo : E_DisplayBehavior.Default);
  }

  private applyGridLayout(params: Params): void {
    let columns = parseInt(params['columns'], 10);
    let rows = parseInt(params['rows'], 10);

    if ((!columns || !rows) && params['size']) {
      const [w, h] = params['size'].split('x').map((n: string) => parseInt(n, 10));
      columns = w;
      rows = h;
    }

    const allowedLayouts: I_GridLayout[] = [
      { columns: 5, rows: 4 },
      { columns: 4, rows: 3 }
    ];

    const valid = allowedLayouts.find(l => l.columns === columns && l.rows === rows);

    if (valid) {
      this.responsive.setGridLayout(valid);
    } else {
      console.warn(`Unsupported grid size: ${columns}x${rows}`);
    }
  }

  private showKioskInfoDialog(): void {
    this.overlay.openDialog({
      component: InfoDialogComponent,
      blockBackground: true,
      componentData: {
        info: $localize`:@@InfoTextKiosk:La aplicación va a ejecutarse en modo quiosco.`,
        buttonText: $localize`:@@ButtonTextKiosk:Aceptar`,
      },
    }).pipe(takeUntil(this.destroy$)).subscribe(async (dialogResp) => {
      if (dialogResp.status === EDialogStatus.CLOSED) {
        await this.fullScreenService.enterFullScreen();
      }
    });
  }

  private handleAuthToken(token: string): void {
    // logic is in auth interceptor now
  }

  private setupTitle(): void {
    this.titleStrategy.setupTitle({
      prefix: 'Adas ',
      preSplitter: '',
      currentTitle: 'Web Display',
      postSplitter: '',
      suffix: '',
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}